import { useEffect, useRef, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import {
  useVehicleOperationServiceV1BatchCreateVehicleOperationMutation,
  useVehicleOperationServiceV1GetVehicleOperationFuelTypesQuery,
  useVehicleOperationServiceV1GetVehicleOperationTripTypesQuery,
  useVehicleOperationServiceV1GetVehicleOperationVehicleAuxiliariesQuery,
  useVehicleOperationServiceV1GetVehicleOperationVehicleModelsQuery,
  useVehicleOperationServiceV1GetVehicleOperationVehicleTypesQuery,
} from "_services/modules/vehicle_operation";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";
interface VehicleOperationItem {
  vehicleTypeId: string;
  vehicleTypeName: string;
  vehicleModelId: string;
  vehicleModelName: string;
  fuelTypeId: string;
  fuelTypeName: string;
  vehicleAttachmentIds: string[];
  vehicleAttachmentNames: string[];
  tripTypeId: string;
  tripTypeName: string;
  fuelUsage: string;
  avgSpeed: string;
  workingHours: string;
  status: string;
}

export interface AddVehicleOperationFormValues {
  vehicleTypeId: string;
  vehicleModelId: string;
  fuelTypeId: string;
  vehicleAttachmentIds: string[];
  tripTypeId: string;
  fuelUsage: string;
  avgSpeed: string;
  workingHours: string;
  status: string;
  items: VehicleOperationItem[];
}

const schema = yup.object({
  vehicleTypeId: yup.string().required("Vehicle Type is required"),
  vehicleModelId: yup.string().required("Vehicle Model is required"),
  fuelTypeId: yup.string().required("Fuel Type is required"),
  vehicleAttachmentIds: yup.array().of(yup.string().required()).default([]),
  tripTypeId: yup.string().required("Trip Type is required"),
  fuelUsage: yup.string().required("Fuel Usage is required").test(
    "is-positive-number",
    "Must be a positive number",
    (value) => {
      if (!value) return false;
      const num = Number(value);
      return !isNaN(num) && num > 0;
    }
  ),
  avgSpeed: yup.string().required("Avg. Speed is required").test(
    "is-positive-number",
    "Must be a positive number",
    (value) => {
      if (!value) return false;
      const num = Number(value);
      return !isNaN(num) && num > 0;
    }
  ),
  workingHours: yup.string().required("Working Hours is required").test(
    "is-positive-number",
    "Must be a positive number",
    (value) => {
      if (!value) return false;
      const num = Number(value);
      return !isNaN(num) && num > 0;
    }
  ),
  status: yup.string().required("Status is required"),
  items: yup.array().of(
    yup.object({
      vehicleTypeId: yup.string().required(),
      vehicleTypeName: yup.string().required(),
      vehicleModelId: yup.string().required(),
      vehicleModelName: yup.string().required(),
      fuelTypeId: yup.string().required(),
      fuelTypeName: yup.string().required(),
      vehicleAttachmentIds: yup.array().of(yup.string().required()).required(),
      vehicleAttachmentNames: yup.array().of(yup.string().required()).required(),
      tripTypeId: yup.string().required(),
      tripTypeName: yup.string().required(),
      fuelUsage: yup.string().required(),
      avgSpeed: yup.string().required(),
      workingHours: yup.string().required(),
      status: yup.string().required(),
    })
  ).required(),
}).required();

const useAddVehicleOperationForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("vehicleOperation");

  const [batchCreateVehicleOperationConfig] = useVehicleOperationServiceV1BatchCreateVehicleOperationMutation();

  const { data: vehicleTypesData } = useVehicleOperationServiceV1GetVehicleOperationVehicleTypesQuery();
  const { data: fuelTypesData } = useVehicleOperationServiceV1GetVehicleOperationFuelTypesQuery();
  const { data: vehicleAuxiliaryData } = useVehicleOperationServiceV1GetVehicleOperationVehicleAuxiliariesQuery();

  const vehicleTypes = vehicleTypesData?.vehicle_types ?? [];
  const fuelTypes = fuelTypesData?.fuel_types ?? [];
  const attachmentOptions = vehicleAuxiliaryData?.vehicle_auxiliaries?.map((a) => ({
    id: a.id ?? "",
    name: a.name ?? "",
  })) ?? [];

  const {
    control,
    register,
    trigger,
    getValues,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<AddVehicleOperationFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      vehicleTypeId: "",
      vehicleModelId: "",
      fuelTypeId: "",
      vehicleAttachmentIds: [],
      tripTypeId: "",
      fuelUsage: "",
      avgSpeed: "",
      workingHours: "",
      status: "active",
      items: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "items",
  });

  const skipModelReset = useRef(false);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>({});

  const vehicleTypeId = watch("vehicleTypeId");
  const selectedVehicleType = vehicleTypes.find((vt) => vt.id === vehicleTypeId);
  const showAttachment = !!(selectedVehicleType as any)?.is_add_on;

  const { data: vehicleModelsData } = useVehicleOperationServiceV1GetVehicleOperationVehicleModelsQuery(
    { vehicleTypeId },
    { skip: !vehicleTypeId }
  );
  const { data: tripTypesData } = useVehicleOperationServiceV1GetVehicleOperationTripTypesQuery(
    { vehicleTypeId },
    { skip: !vehicleTypeId }
  );

  const vehicleModelOptions = vehicleModelsData?.vehicle_models ?? [];
  const tripTypes = tripTypesData?.trip_types ?? [];

  useEffect(() => {
    if (skipModelReset.current) {
      skipModelReset.current = false;
      return;
    }
    setValue("vehicleModelId", "");
    setValue("vehicleAttachmentIds", []);
  }, [vehicleTypeId, setValue]);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-add"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  const toggleExpand = (index: number) => {
    setExpandedItems((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const getDisplayNames = () => {
    const vehicleTypeId = getValues("vehicleTypeId");
    const vehicleModelId = getValues("vehicleModelId");
    const fuelTypeId = getValues("fuelTypeId");
    const tripTypeId = getValues("tripTypeId");
    const attachmentIds = getValues("vehicleAttachmentIds");

    return {
      vehicleTypeName: vehicleTypes.find((vt) => vt.id === vehicleTypeId)?.name ?? "",
      vehicleModelName: vehicleModelOptions.find((vm) => vm.id === vehicleModelId)?.name ?? "",
      fuelTypeName: fuelTypes.find((ft) => ft.id === fuelTypeId)?.name ?? "",
      tripTypeName: tripTypes.find((tt) => tt.id === tripTypeId)?.name ?? "",
      vehicleAttachmentNames: attachmentOptions
        .filter((a) => attachmentIds.includes(a.id))
        .map((a) => a.name),
    };
  };

  const handleAddToList = async () => {
    const fieldsToValidate: (keyof AddVehicleOperationFormValues)[] = [
      "vehicleTypeId", "vehicleModelId", "fuelTypeId", "tripTypeId",
      "fuelUsage", "avgSpeed", "workingHours", "status",
    ];
    const isValid = await trigger(fieldsToValidate);
    if (!isValid) return;

    const values = getValues();
    const names = getDisplayNames();

    // Duplicate check: (vehicleTypeId, vehicleModelId, tripTypeId)
    const isDuplicate = fields.some(
      (item, index) =>
        item.vehicleTypeId === values.vehicleTypeId &&
        item.vehicleModelId === values.vehicleModelId &&
        item.tripTypeId === values.tripTypeId &&
        index !== editingIndex
    );

    if (isDuplicate) {
      setError("tripTypeId", {
        type: "validate",
        message: t("error-duplicate-value"),
      });
      return;
    }

    const newItem: VehicleOperationItem = {
      vehicleTypeId: values.vehicleTypeId,
      vehicleTypeName: names.vehicleTypeName,
      vehicleModelId: values.vehicleModelId,
      vehicleModelName: names.vehicleModelName,
      fuelTypeId: values.fuelTypeId,
      fuelTypeName: names.fuelTypeName,
      vehicleAttachmentIds: values.vehicleAttachmentIds,
      vehicleAttachmentNames: names.vehicleAttachmentNames,
      tripTypeId: values.tripTypeId,
      tripTypeName: names.tripTypeName,
      fuelUsage: values.fuelUsage,
      avgSpeed: values.avgSpeed,
      workingHours: values.workingHours,
      status: values.status,
    };

    if (editingIndex !== null) {
      update(editingIndex, newItem);
      setEditingIndex(null);
    } else {
      append(newItem);
    }

    resetForm();
  };

  const resetForm = () => {
    setValue("vehicleTypeId", "");
    setValue("vehicleModelId", "");
    setValue("fuelTypeId", "");
    setValue("vehicleAttachmentIds", []);
    setValue("tripTypeId", "");
    setValue("fuelUsage", "");
    setValue("avgSpeed", "");
    setValue("workingHours", "");
    setValue("status", "active");
  };

  const handleEditItem = (index: number) => {
    const item = fields[index];
    setEditingIndex(index);
    skipModelReset.current = true;
    setValue("vehicleTypeId", item.vehicleTypeId);
    setValue("vehicleModelId", item.vehicleModelId);
    setValue("fuelTypeId", item.fuelTypeId);
    setValue("vehicleAttachmentIds", item.vehicleAttachmentIds);
    setValue("tripTypeId", item.tripTypeId);
    setValue("fuelUsage", item.fuelUsage);
    setValue("avgSpeed", item.avgSpeed);
    setValue("workingHours", item.workingHours);
    setValue("status", item.status);
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
    if (editingIndex === index) {
      setEditingIndex(null);
      resetForm();
    } else if (editingIndex !== null && index < editingIndex) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const handleDiscardChanges = () => {
    setEditingIndex(null);
    resetForm();
  };

  const handleSave = async () => {
    try {
      const payload = fields.map((f) => ({
        vehicle_type_id: f.vehicleTypeId,
        vehicle_model_id: f.vehicleModelId,
        fuel_type_id: f.fuelTypeId,
        vehicle_attachment_ids: f.vehicleAttachmentIds,
        trip_type_id: f.tripTypeId,
        fuel_usage: Number(f.fuelUsage),
        avg_speed: Number(f.avgSpeed),
        rest_hours: 0,
        working_hours: Number(f.workingHours),
        status: f.status,
      }));
      await batchCreateVehicleOperationConfig({
        vehicleOperationV1BatchCreateVehicleOperationReq: {
          vehicle_operations: payload,
        },
      }).unwrap();
      toast.success(t("toast-create-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error) {
      errorHandler(error);
      setShowSaveModal(false);
    }
  };

  const handleCancel = () => {
    if (fields.length > 0) {
      setShowCancelModal(true);
    } else {
      navigate(-1);
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  return {
    control,
    register,
    errors,
    fields,
    editingIndex,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    showAttachment,
    vehicleModelOptions,
    expandedItems,
    toggleExpand,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    handleConfirmCancel,
    t,
    // Dropdown data
    vehicleTypes,
    fuelTypes,
    tripTypes,
    attachmentOptions,
  };
};

export default useAddVehicleOperationForm;
